import { loadStripe } from "@stripe/stripe-js";

class StripeExpressPay {

  /*
    Class to handle Stripe Express Checkout with Apple Pay and Google Pay integration.
    It includes logic to restrict shipping addresses to mainland UK only,
    excluding Scottish Highlands, Channel Islands, and Northern Ireland,
    Test adddesses are:
     - Scottish test address: 1 High Street, Thurso, KW14 7AA
     - Channel Islands test address: 10 Castle Street, St Helier, Jersey, JE2 3RT
     - Northern Ireland test address: 5 High Street, Belfast, BT1 2AA
     - UK mainland test address: 6 Pancras Square, London, N1C 4AG
    Test product variants:
     - https://www.cosmetify.local/chloe-nomade-eau-de-parfum/
     - https://www.cosmetify.local/jo-malone-london-nectarine-blossom-honey-eau-de-parfum/
     - https://www.cosmetify.local/elemis-pro-collagen-night-cream/?vf=3720813
     - https://www.cosmetify.local/estee-lauder-double-wear-stay-in-place-foundation-makeup-spf10/?vf=4844216
  */

  constructor(
      stripePublicKey,
      affiliateId,
      productVariantId,
      shippingOptions,
      paymentDisplayName,
      productVariantPrice,
      paymentRequestOptions,) {

    this.debug = false;

    this.stripePublicKey = stripePublicKey;
    this.affiliateId = affiliateId;
    this.paymentRequestOptions = paymentRequestOptions;
    this.productVariantId = productVariantId;
    this.shippingOptions = shippingOptions || [];
    this.paymentDisplayName = paymentDisplayName || "Cosmetify";
    this.baseAmount = productVariantPrice;

    this.stripe = null;
    this.shippingOptionId = null;
    this.paymentRequest = null;
    this.applePayButton = null;
    this.googlePayButton = null;
  }

  async init() {
    /*
      Initializes the Stripe instance and sets up the payment request
    */
    this.stripe = await loadStripe(this.stripePublicKey);
    if (!this.stripe) {
      (this.debug) && console.error("Stripe failed to initialize.");
      return;
    }
    this.paymentRequest = this.stripe.paymentRequest(
      this.paymentRequestOptions
    );
  }

  async createApplePayButton(containerId) {
    /*
      Creates and mounts the Apple Pay button if available
    */
    if (!this.paymentRequest) {
      (this.debug) && console.error("Payment Request is not initialized.");
      return;
    }
    const result = await this.paymentRequest.canMakePayment();
    (this.debug) && console.log("Apple Pay canMakePayment result:", result);
    if (result && result.applePay) {
      this.applePayButton = this.stripe
        .elements()
        .create("paymentRequestButton", {
          paymentRequest: this.paymentRequest,
          style: {
            paymentRequestButton: {
              type: "buy", // or 'default'
              theme: "dark",
              height: "44px",
            },
          },
        });
      this.applePayButton.mount(`#${containerId}`);
    } else {
      (this.debug) && console.warn("Apple Pay is not available on this device/browser.");
    }
  }

  async createGooglePayButton(containerId) {
    /*
      Creates and mounts the Google Pay button if available
    */
    if (!this.paymentRequest) {
      (this.debug) && console.error("Payment Request is not initialized.");
      return;
    }
    const result = await this.paymentRequest.canMakePayment();
    (this.debug) && console.log("Google Pay canMakePayment result:", result);
    if (result && result.googlePay) {
      this.googlePayButton = this.stripe
        .elements()
        .create("paymentRequestButton", {
          paymentRequest: this.paymentRequest,
          style: {
            paymentRequestButton: {
              type: "buy", // or 'default'
              theme: "dark",
              height: "44px",
            },
          },
        });
      this.googlePayButton.mount(`#${containerId}`);
    } else {
      (this.debug) && console.warn("Google Pay is not available on this device/browser.");
    }
  }

  getCountryId(postalCode, countryCode) {
    const countryCodeIdMap = { GB: 1, NI: 13, SH: 15, CI: 18 };
    return this.isChannelIslands(postalCode, countryCode) ? countryCodeIdMap.CI
      : this.isNorthernIreland(postalCode, countryCode) ? countryCodeIdMap.NI
      : this.isScottishHighlands(postalCode, countryCode) ? countryCodeIdMap.SH
      : countryCodeIdMap.GB;
  }

  isChannelIslands(postalCode, countryCode) {
    /*
      Returns true if the address is in the Channel Islands
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("je") || postalCode.toLowerCase().startsWith("gy");
  }

  isScottishHighlands(postalCode, countryCode) {
    /*
      Returns true if the address is in the Scottish Highlands
    */
    if (countryCode !== "GB") return false;
    return /^HS|IV|KA27|KW|PA20|PA21|PA38|PH41|PH42|ZE/.test(postalCode.toUpperCase());
  }

  isNorthernIreland(postalCode, countryCode) {
    /*
      Returns true if the address is in Northern Ireland
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("bt");
  }

  isIsleOfMan(postalCode, countryCode) {
    /*
      Returns true if the address is in the Isle of Man
    */
    if (countryCode !== "GB") return false;
    return postalCode.toLowerCase().startsWith("im");
  }

  getAvailableShippingOptions(postalCode, countryCode) {
    /* 
      Returns shipping options based on country and postal code
      Utilises the shippingOptions array defined in the constructor
      Returns: Array of available shipping options for the stripe payment request
    */
    let availableShippingOptions = [];
    (countryCode === "GB") && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "1")));
    this.isChannelIslands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "18")));
    this.isScottishHighlands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "15")));
    this.isNorthernIreland(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter(opt => opt.countries.some(c => c.id === "13")));
    return availableShippingOptions.map(opt => ({
      id: opt.id,
      label: opt.label,
      detail: opt.detail,
      amount: opt.amount,
    }));
  }

  validateShippingAddress(countryCode, countryId, postalCode) {
    /*
      Verifies if the shipping address is valid
      The country must be in the allowed list and match the postal code restrictions
      Returns: true if valid, false otherwise
    */
    if (!countryCode || !countryId || !postalCode) return false;
    if (countryId === 18 && this.isChannelIslands(postalCode, countryCode)) return true;
    if (countryId === 15 && this.isScottishHighlands(postalCode, countryCode)) return true;
    if (countryId === 13 && this.isNorthernIreland(postalCode, countryCode)) return true;
    if (countryCode === "GB" && countryId === 1) return true;
    return false;
  }

  splitName(fullName) {
    
    if (!fullName) return { firstName: "", lastName: "" };
    const parts = fullName.trim().split(" ");
    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  }

  handlePaymentMethod() {
    /*
      Sets up event handlers for payment method, shipping address change, and shipping option change
    */
    if (!this.paymentRequest) return;

    this.paymentRequest.on("paymentmethod", async (event) => {
      /*
        Handles the payment method event and processes the payment
      */
      (this.debug) && console.log("paymentmethod event:", event);
      this.clearErrorMessage(document.body);

      const billingDetails = event.paymentMethod.billing_details;

      const payerEmail = event.payerEmail || event.paymentMethod.billing_details.email;
      const payerName = event.payerName;
      const payerPhone = event.payerPhone || "00000000000";
      const paymentMethod = event.paymentMethod;
      const shippingAddress = event.shippingAddress;
      const shippingOption = event.shippingOption;
      const walletName = event.walletName;
      const payerSplitName = this.splitName(payerName);

      (this.debug) && console.log("Confirming payment with client secret:", billingDetails);
      (this.debug) && console.log("payerEmail:", payerEmail);
      (this.debug) && console.log("payerName:", payerName);
      (this.debug) && console.log("payerPhone:", payerPhone);
      (this.debug) && console.log("paymentMethod:", paymentMethod);
      (this.debug) && console.log("shippingAddress:", shippingAddress);
      (this.debug) && console.log("shippingOption:", shippingOption);
      (this.debug) && console.log("walletName:", walletName);

      const countryId = this.getCountryId(this.deliveryInfo.postalCode, event.shippingAddress.country);

      if(!this.validateShippingAddress(event.shippingAddress.country, countryId, shippingAddress.postalCode)) {
        this.setErrorMessage(document.body, "Payment failure: Shipping address is outside UK.");
        (this.debug) && console.error("validateShippingAddress failed:", shippingAddress);
        event.complete("fail");
        return;
      }

      try {
        let basketUpdateResponse = await this.updateBasket({ product_variant_id: this.productVariantId });
        if (!basketUpdateResponse.success) {
          this.setErrorMessage(document.body, "Payment failure: Unable to update basket.");
          (this.debug) && console.error("updateBasket failed:", basketUpdateResponse);
          event.complete("fail");
          return;
        }
        (this.debug) && console.log("basketUpdateResponse:", basketUpdateResponse);
        
        const deliveryOk = await this.setDeliveryInfoOnOrder({ country: countryId, [this.affiliateId]: this.shippingOptionId });
        (this.debug) && console.log(`setDeliveryInfoOnOrder params: country: ${countryId}, [${this.affiliateId}]: ${this.shippingOptionId}`);
        if (!deliveryOk) {
          this.setErrorMessage(document.body, "Payment failure: Unable to set delivery information.");
          (this.debug) && console.error("setDeliveryInfoOnOrder failed:", deliveryOk);
          event.complete("fail");
          return;
        }
        (this.debug) && console.log("setDeliveryInfoOnOrder:", deliveryOk, this.deliveryInfo);

        const basketOk = await this.basketChecker(basketUpdateResponse.order_id, [this.productVariantId]);
        if (!basketOk) {
          this.setErrorMessage(document.body, "Payment failure: Basket has a problem.");
          (this.debug) && console.error("basketChecker failed:", basketOk);
          event.complete("fail");
          return;
        }
        (this.debug) && console.log("Basket OK response:", basketOk);

        const paymentIntentPayload = {
          "Email": payerEmail,
          "FirstName": payerSplitName.firstName,
          "LastName": payerSplitName.lastName,
          "PhoneNumber": payerPhone,
        };
        const paymentIntentResult = await this.generatePaymentIntent(basketUpdateResponse.order_id, paymentIntentPayload);
        if (paymentIntentResult.error || !paymentIntentResult.client_secret) {
          this.setErrorMessage(document.body, 'Payment failure: Unable to create payment intent.');
          (this.debug) && console.error("generatePaymentIntent failed:", paymentIntentResult);
          event.complete("fail");
          return;
        }
        (this.debug) && console.log("Stripe intent response", paymentIntentResult);

        const confirmResult = await this.stripe.confirmCardPayment(paymentIntentResult.client_secret, {
          payment_method: event.paymentMethod.id
        });
        if (confirmResult.error) {
          this.setErrorMessage(document.body, `Payment failure: ${confirmResult.error.message}`);
          event.complete("fail");
          return;
        }
        (this.debug) && console.log("Stripe confirm result", confirmResult);

        if (confirmResult.paymentIntent && confirmResult.paymentIntent.status === "requires_capture") {
          const processOrderPayload = {
            order_id: basketUpdateResponse.order_id,
            payment_intent_client_secret: paymentIntentResult.client_secret,
            FirstName: payerSplitName.firstName,
            LastName: payerSplitName.lastName,
            Email: payerEmail,
            PhoneNumber: payerPhone,
            ShipAddress1: shippingAddress.addressLine[0],
            ShipAddress2: shippingAddress.addressLine[1] || "",
            ShipCounty: shippingAddress.region || shippingAddress.state || "",
            ShipPostcode: shippingAddress.postalCode,
            ShipCountry: countryId,
            BillAddress1: billingDetails.address.line1,
            BillAddress2: billingDetails.address.line2,
            BillCounty: billingDetails.address.state,
            BillPostcode: billingDetails.address.postal_code,
            BillCountry: countryId,
            complete: true,
          };
          const processOrderResult = await this.processOrder(processOrderPayload);
          if (processOrderResult.startsWith("error:")) {
            this.setErrorMessage(document.body, 'Payment failure: Promblem processing order.');
            (this.debug) && console.error("processOrder failed:", processOrderResult);
            event.complete("fail");
            return;
          }
          (this.debug) && console.log("processOrderResult:", processOrderResult);

          event.complete("success");
          window.location.href = "/basket/confirmation/";

        } else {
          this.setErrorMessage(document.body, "Payment was not successful.");
          event.complete("fail");
          return;
        }
      } catch (err) {
        console.error("Failed to update basket for express checkout:", err);
        this.setErrorMessage(document.body, "Payment failure: An unexpected error occurred.");
        event.complete("fail");
      }
    });

    this.paymentRequest.on("shippingaddresschange", (event) => {
      /*
        Handles shipping address changes and updates available shipping options
      */
      (this.debug) && console.log("shippingaddresschange event:", event);
      this.deliveryInfo = {
        country: event.shippingAddress.country,
        postalCode: event.shippingAddress.postalCode || "",
        addressLine1: event.shippingAddress.addressLine1 || event.shippingAddress.line1 || "",
        addressLine2: event.shippingAddress.addressLine2 || event.shippingAddress.line2 || "",
        city: event.shippingAddress.city || "",
        region: event.shippingAddress.region || event.shippingAddress.state || "",
        recipient: event.shippingAddress.recipient || event.shippingAddress.name || "",
        phone: event.shippingAddress.phone || "",
      };

      const availableShippingOptions = this.getAvailableShippingOptions(
        this.deliveryInfo.postalCode,
        this.deliveryInfo.country,
      );
      
      if (!availableShippingOptions || availableShippingOptions.length === 0) {
        event.updateWith({
          shippingOptions: [],
          status: "invalid_shipping_address",
        });
        return;
      }

      event.updateWith({
        shippingOptions: availableShippingOptions ? availableShippingOptions : [],
        status: "success",
      });

      this.shippingOptionId = availableShippingOptions[0].id;
    });

    this.paymentRequest.on("shippingoptionchange", (event) => {
      /*
        Handles shipping option changes and updates the total amount
      */
      (this.debug) && console.log("shippingoptionchange event:", event);
      const availableShippingOptions = this.getAvailableShippingOptions(
        this.deliveryInfo.postalCode,
        this.deliveryInfo.country,
      );

      if (event.shippingOption && event.shippingOption.id) {
        this.shippingOptionId = event.shippingOption.id;
      } else {
        this.shippingOptionId = null;
      }

      const selectedOption = availableShippingOptions.find(
        (opt) => opt.id === this.shippingOptionId
      );

      const newTotal = this.baseAmount + (selectedOption ? selectedOption.amount : 0);

      event.updateWith({
        status: "success",
        shippingOptions: availableShippingOptions,
        total: {
          label: this.paymentDisplayName,
          amount: newTotal,
        },
      });
    });
  }

  async updateBasket(data) {
    const response = await fetch(`/express-checkout/basket/update/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  }

  async basketChecker(orderId, pvids) {
    const response = await fetch(`/basket/api/basket-checker/${orderId}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify({ pvids: pvids }),
    });
    const result = await response.json();
    if (!result['happy_check']) {
      console.warn("Basket check failed:", result);
      return false;
    }
    if (result['order_processed']) {
      console.warn("Order has already been processed. Redirecting to confirmation page.");
      return false;
    }
    return true;
  }

  async setDeliveryInfoOnOrder(deliveryInfo) {
    const response = await fetch(`/basket/api/set-delivery-info/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(deliveryInfo),
    });
    return response.ok;
  }

  async generatePaymentIntent(orderId, data) {
    const response = await fetch(
      `/basket/api/generate-payment-intent/${orderId}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(data),
      }
    );
    return await response.json();
  }

  async processOrder(data) {
    const response = await fetch(`/basket/api/process-order/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRFToken": getCSRFToken(),
      },
      body: JSON.stringify(data),
    });
    return await response.text();
  }

  setErrorMessage(container, message) {
    const errorMessageDiv = container.querySelector('.express-error-message');
    if (errorMessageDiv) {
      errorMessageDiv.textContent = message;
      errorMessageDiv.style.display = 'block';
    }
  }

  clearErrorMessage(container) {
    const errorMessageDiv = container.querySelector('.express-error-message');
    if (errorMessageDiv) {
      errorMessageDiv.textContent = '';
      errorMessageDiv.style.display = 'none';
    }
  }

  getFirstSelectedShippingOption(availableShippingOptions) {
    if (Array.isArray(availableShippingOptions) && availableShippingOptions.length > 0) {
      return availableShippingOptions[0];
    }
    return null;
  }

  static async initFromContainer(container) {
    /*
      Initializes StripeExpressPay from a given container element
    */
    const stripePublicKey = container.getAttribute("data-public-key");
    const affiliateId = parseInt(container.querySelector(".shipping-option")?.getAttribute("data-affiliate-id"), 10);
    const productVariantId = parseInt(container.getAttribute("data-product-variant-id"), 10);
    const productName = container.getAttribute("data-product-name");
    const productVariantName = container.getAttribute("data-product-variant-name");
    const productVariantPrice = parseInt(container.getAttribute("data-product-variant-price"), 10);
    const productVariantInStock = container.getAttribute("data-in-stock") === "True";
    const numberDeliveryOptions = parseInt(container.getAttribute("data-number-delivery-options"), 10);
    const paymentDisplayName = productVariantName ? `${productName} - ${productVariantName}` : productName;

    if (!productVariantInStock) {
      console.warn("Product variant is out of stock. Express Pay buttons will not be initialized.");
      return;
    }

    if (numberDeliveryOptions === 0) {
      console.warn("No delivery options available. Express Pay buttons will not be initialized.");
      return;
    }

    const shippingOptions = [];
    container.querySelectorAll(".shipping-option").forEach((option) => {
      shippingOptions.push({
        id: option.getAttribute("data-service-id"),
        label: option.getAttribute("data-label"),
        detail: option.getAttribute("data-detail"),
        amount: parseInt(option.getAttribute("data-gross-price"), 10),
        countries: Array.from(option.querySelectorAll(".shipping-country")).map(countryDiv => ({
          id: countryDiv.getAttribute("data-country-id"),
          code: countryDiv.getAttribute("data-country-code"),
          name: countryDiv.getAttribute("data-country-name"),
        })),
      });
    });

    const defaultShipping = shippingOptions.length > 0 ? shippingOptions[0].amount : 0;
    const initialTotal = productVariantPrice + defaultShipping;

    const paymentRequestOptions = {
      country: "GB",
      currency: "gbp",
      total: { label: paymentDisplayName, amount: initialTotal },
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: true,
      shippingOptions: [],
    };

    const expressPay = new StripeExpressPay(
      stripePublicKey,
      affiliateId,
      productVariantId,
      shippingOptions,
      paymentDisplayName,
      productVariantPrice,
      paymentRequestOptions,
    );

    await expressPay.init();

    await expressPay.createApplePayButton(
      `stripe-apple-pay-${productVariantId}`
    );

    await expressPay.createGooglePayButton(
      `stripe-google-pay-${productVariantId}`
    );

    expressPay.handlePaymentMethod();
  }
}

export default StripeExpressPay;
